import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Phone, Check, ArrowRight } from "lucide-react";
import axios from "axios";
import SEO, { LOCATION_KEYWORDS } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WindowsPage = () => {
  const [windowProjects, setWindowProjects] = useState([]);

  const fetchWindowProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects?category=windows`);
      setWindowProjects(response.data.slice(0, 3));
    } catch {
      // Silently handle error
    }
  }, []);

  useEffect(() => {
    fetchWindowProjects();
  }, [fetchWindowProjects]);

  const windowTypes = [
    {
      title: "Casement Windows",
      description: "Classic hinged windows that open outward, perfect for maximum ventilation and easy cleaning.",
      image: "https://customer-assets.emergentagent.com/job_orange-glaze/artifacts/wj0yjyop_casement%20window.jpg",
    },
    {
      title: "Bay Windows",
      description: "Elegant bay windows that extend outward, creating extra space and letting in more natural light.",
      image: "https://customer-assets.emergentagent.com/job_orange-glaze/artifacts/znotsi4x_bay%20window.jpg",
    },
    {
      title: "Tilt & Turn Windows",
      description: "Versatile windows that can tilt for ventilation or turn for full opening access.",
      image: "https://customer-assets.emergentagent.com/job_orange-glaze/artifacts/quilhokz_tilt%20and%20turn.jpg",
    },
    {
      title: "Sash Windows",
      description: "Traditional sliding sash windows with modern performance and security features.",
      image: "https://customer-assets.emergentagent.com/job_orange-glaze/artifacts/7noca1ed_sash%20windows.jpg",
    },
  ];

  const benefits = [
    "A-rated energy efficiency",
    "Reduce heat loss by up to 50%",
    "Lower energy bills",
    "Improved sound insulation",
    "Enhanced security features",
    "10 year guarantee",
    "Wide range of styles & colours",
    "Professional installation",
  ];

  return (
    <div data-testid="windows-page" className="pt-32 lg:pt-36">
      <SEO 
        title="Double Glazing Windows Lanarkshire | A-Rated uPVC Windows Motherwell, Glasgow"
        description="A-rated uPVC double glazing windows in Motherwell, Hamilton, Glasgow & Lanarkshire. Bay windows, casement, tilt & turn, sash windows. Professional installation, 10 year guarantee. Free quotes - call 07917 181597"
        keywords={`double glazing windows, A-rated windows, uPVC windows, bay windows, casement windows, tilt and turn windows, sash windows, energy efficient windows, window installation, window replacement, glazing repairs, double glazing Motherwell, double glazing Hamilton, windows Glasgow, windows Lanarkshire, ${LOCATION_KEYWORDS}`}
        canonical="/windows"
      />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0A0A0A]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://static.wixstatic.com/media/4dd2ec_0e077c66652d4d22b5140327c134eaf1~mv2.jpg/v1/fill/w_605,h_992,al_c,q_85,enc_avif,quality_auto/4dd2ec_0e077c66652d4d22b5140327c134eaf1~mv2.jpg')`,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1
              className="text-4xl sm:text-5xl font-black text-white mb-6"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              DOUBLE GLAZING{" "}
              <span className="text-[#FF6B00]">WINDOWS</span>
            </h1>
            <p className="text-gray-300 text-lg mb-8">
              From single windows to a complete home transformation, we can tailor
              a package to suit every budget. Our energy-efficient A-rated windows
              help reduce heat loss, lower energy bills, and improve sound insulation. Pair with our <Link to="/doors" className="text-[#FF6B00] hover:underline">composite doors</Link> for a full home upgrade.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="windows-cta"
                className="bg-[#FF6B00] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#E65000] transition-all text-center"
              >
                Get Free Quote
              </Link>
              <a
                href="tel:07917181597"
                className="bg-transparent border-2 border-white text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-white hover:text-[#0A0A0A] transition-all flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                07917 181597
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2
                className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-6"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                WHY CHOOSE OUR{" "}
                <span className="text-[#FF6B00]">WINDOWS?</span>
              </h2>
              <p className="text-[#525252] mb-8">
                Our high-performance double glazed windows are designed to make
                your home warmer, quieter, and more secure. Using only the best
                Rehau and Veka profiles, we guarantee quality and longevity. Complete your home with a new <Link to="/doors" className="text-[#FF6B00] hover:underline">front door</Link> or a bespoke <Link to="/outbuilds" className="text-[#FF6B00] hover:underline">garden room</Link>. See our <Link to="/recent-work" className="text-[#FF6B00] hover:underline">recent installations</Link>.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit) => (
                  <div
                    key={benefit}
                    data-testid={`benefit-${benefit.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 bg-[#FF6B00] flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#0A0A0A]">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://static.wixstatic.com/media/4dd2ec_0e077c66652d4d22b5140327c134eaf1~mv2.jpg/v1/fill/w_605,h_992,al_c,q_85,enc_avif,quality_auto/4dd2ec_0e077c66652d4d22b5140327c134eaf1~mv2.jpg"
                alt="Double Glazed Windows"
                className="w-full max-w-md mx-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#FF6B00] text-white p-6">
                <p className="font-black text-2xl">A-RATED</p>
                <p className="text-sm">Energy Efficiency</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Window Types Section */}
      <section data-testid="window-types-section" className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-4"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              WINDOW <span className="text-[#FF6B00]">STYLES</span>
            </h2>
            <p className="text-[#525252] max-w-2xl mx-auto">
              We offer a wide range of window styles to suit any home, from
              traditional to contemporary designs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {windowTypes.map((type) => (
              <div
                key={type.title}
                data-testid={`window-type-${type.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-white border border-[#E5E5E5] overflow-hidden group hover:border-[#FF6B00] transition-all"
              >
                <div className="aspect-square overflow-hidden bg-[#F5F5F5]">
                  <img
                    src={type.image}
                    alt={type.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3
                    className="text-lg font-black text-[#0A0A0A] mb-2"
                    style={{ fontFamily: "'Chivo', sans-serif" }}
                  >
                    {type.title}
                  </h3>
                  <p className="text-[#525252] text-sm">{type.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Glazing Repairs Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://customer-assets.emergentagent.com/job_orange-glaze/artifacts/chacn3wz_bay%20window.jpg"
                alt="Glazing Repairs"
                className="w-full h-auto object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2
                className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-6"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                GLAZING <span className="text-[#FF6B00]">REPAIRS</span>
              </h2>
              <p className="text-[#525252] mb-6">
                Misty or faulty windows? In most cases, we can replace the glass
                without the need to replace the frames, saving you time and money. <Link to="/contact" className="text-[#FF6B00] hover:underline">Contact us</Link> for a quick response.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Misted double glazing replacement",
                  "Broken glass repairs",
                  "Failed seal replacement",
                  "Same day response available",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#FF6B00] flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#0A0A0A]">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-[#FF6B00] font-bold uppercase tracking-wide hover:gap-3 transition-all"
              >
                Get a Repair Quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Work */}
      {windowProjects.length > 0 && (
        <section className="py-20 bg-[#F5F5F5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <h2
                className="text-3xl font-black text-[#0A0A0A]"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                RECENT <span className="text-[#FF6B00]">WINDOW WORK</span>
              </h2>
              <Link
                to="/recent-work"
                className="text-[#FF6B00] font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:gap-3 transition-all"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {windowProjects.map((project) => (
                <div
                  key={project.id}
                  data-testid={`window-project-${project.id}`}
                  className="bg-white border border-[#E5E5E5] overflow-hidden group hover:border-[#FF6B00] transition-all"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-[#0A0A0A]">{project.title}</h3>
                    {project.location && (
                      <p className="text-sm text-[#525252]">{project.location}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-[#FF6B00] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl font-black text-white mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            GET A FREE WINDOW QUOTE TODAY
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            <Link to="/contact" className="text-white underline hover:text-[#0A0A0A]">Contact us</Link> for a free, no-obligation quote on new windows or glazing
            repairs. We also install <Link to="/doors" className="text-white underline hover:text-[#0A0A0A]">composite doors</Link> and build <Link to="/outbuilds" className="text-white underline hover:text-[#0A0A0A]">garden rooms</Link> across Lanarkshire, Glasgow, and all surrounding areas. Check our <Link to="/reviews" className="text-white underline hover:text-[#0A0A0A]">customer reviews</Link>.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#0A0A0A] hover:text-white transition-all"
          >
            Request Quote
          </Link>
        </div>
      </section>

      {/* Comprehensive Window Information Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2
                className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-6"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                UNDERSTANDING <span className="text-[#FF6B00]">DOUBLE GLAZING</span>
              </h2>
              <p className="text-[#525252] mb-4">
                Double glazing consists of two panes of glass with a gap between them, creating an insulating barrier that significantly 
                reduces heat transfer. This technology has revolutionised home comfort in Lanarkshire and across Scotland, helping 
                homeowners reduce their energy bills while enjoying a warmer, quieter living environment.
              </p>
              <p className="text-[#525252] mb-4">
                Modern A-rated double glazing windows can reduce heat loss by up to 50% compared to single glazed windows. This means 
                your heating system works more efficiently, keeping your home comfortable during cold Scottish winters while reducing 
                your carbon footprint and energy costs.
              </p>
              <p className="text-[#525252] mb-4">
                Beyond energy efficiency, double glazing provides excellent sound insulation - particularly beneficial if you live 
                near busy roads in areas like Motherwell, Hamilton, or Glasgow city centre. The dual-pane construction also adds an 
                extra layer of security, making your home more resistant to break-ins.
              </p>
              <p className="text-[#525252]">
                At C.Glancey Double Glazing, we use premium Rehau and Veka uPVC profiles that offer superior thermal performance, 
                durability, and aesthetic appeal. Our windows are available in a wide range of colours and finishes to complement 
                any property style, from traditional Scottish sandstone houses to modern new builds.
              </p>
            </div>
            <div>
              <h3
                className="text-xl font-black text-[#0A0A0A] mb-4"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                WINDOW ENERGY RATINGS EXPLAINED
              </h3>
              <p className="text-[#525252] mb-4">
                Window Energy Ratings (WER) work similarly to the energy labels you see on household appliances. Windows are rated 
                from A++ (most efficient) to G (least efficient). All our windows achieve a minimum A-rating, ensuring you get the 
                best possible thermal performance for your Lanarkshire home.
              </p>
              <div className="bg-[#F5F5F5] p-6 mb-6">
                <h4 className="font-bold text-[#0A0A0A] mb-3">What Affects Window Energy Efficiency?</h4>
                <ul className="space-y-2 text-[#525252]">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF6B00] mt-1 flex-shrink-0" />
                    <span><strong>Glass type:</strong> Low-E glass reflects heat back into your room</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF6B00] mt-1 flex-shrink-0" />
                    <span><strong>Gap between panes:</strong> Argon gas filling improves insulation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF6B00] mt-1 flex-shrink-0" />
                    <span><strong>Frame material:</strong> Multi-chambered uPVC profiles trap air for better insulation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF6B00] mt-1 flex-shrink-0" />
                    <span><strong>Seals:</strong> Quality weatherseals prevent draughts and water ingress</span>
                  </li>
                </ul>
              </div>
              <h4 className="font-bold text-[#0A0A0A] mb-3">Signs You Need New Windows</h4>
              <ul className="space-y-2 text-[#525252]">
                <li>• Condensation forming between the glass panes (failed seals)</li>
                <li>• Draughts around window frames even when closed</li>
                <li>• Difficulty opening, closing, or locking windows</li>
                <li>• Visible damage, warping, or discolouration</li>
                <li>• Excessive outside noise entering your home</li>
                <li>• Rising energy bills despite efficient heating</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Window Installation Process */}
      <section className="py-16 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-8 text-center"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            OUR WINDOW INSTALLATION <span className="text-[#FF6B00]">PROCESS</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Free Survey", desc: "We visit your home to measure windows and discuss your requirements, styles, and budget." },
              { step: "02", title: "Detailed Quote", desc: "You receive a comprehensive written quote with no hidden costs or pressure to commit." },
              { step: "03", title: "Manufacturing", desc: "Your windows are manufactured to exact specifications using premium Rehau or Veka profiles." },
              { step: "04", title: "Installation", desc: "Our experienced fitters install your windows professionally, typically completing most homes in 1-2 days." },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-[#FF6B00] text-white font-black text-2xl flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold text-[#0A0A0A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#525252]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services Links */}
      <section className="py-12 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-bold text-[#0A0A0A] mb-6">Related Services</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/doors" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              Composite Doors
            </Link>
            <Link to="/outbuilds" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              Garden Rooms
            </Link>
            <Link to="/recent-work" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              View Our Work
            </Link>
            <Link to="/faq" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              Window Installation FAQ
            </Link>
            <Link to="/reviews" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              Customer Reviews
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WindowsPage;
